import React, { useEffect, useMemo, useRef, useState } from "react";
import { Send, Wifi, WifiOff, Check, CheckCheck } from "lucide-react";
import { useI18n } from "../lib/i18n";

/**
 * Reusable real-time chat panel — used by both the customer portal and the
 * admin dashboard. Includes typing indicators and read receipts.
 *
 * Props:
 *  - messages:        array of { id, sender, body, created_at, read?, sender_name }
 *  - onSend(body):    async function to send a message
 *  - onTyping():      optional callback fired (debounced) while the user is typing
 *  - currentSender:   "customer" | "admin"
 *  - peerTyping:      boolean — show typing indicator from the other party
 *  - wsConnected:     bool — drives the connection status pill
 *  - peerName:        label for the other party
 *  - emptyKey:        i18n key for the empty state
 *  - placeholderKey:  i18n key for the textarea placeholder
 *  - sendKey:         i18n key for the send button
 *  - testidPrefix:    e.g. "portal-chat" | "admin-chat"
 *  - introKey:        optional intro/help line above the messages
 */
export default function ChatPanel({
  messages = [],
  onSend,
  onTyping,
  currentSender = "customer",
  peerTyping = false,
  wsConnected = false,
  peerName = "",
  emptyKey = "portal.empty_chat",
  placeholderKey = "portal.chat_placeholder",
  sendKey = "portal.chat_send",
  testidPrefix = "chat",
  introKey,
  fill = false,
}) {
  const { t, lang } = useI18n();
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const typingDebounceRef = useRef(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, peerTyping]);

  const submit = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      await onSend(body);
      setText("");
    } finally {
      setSending(false);
    }
  };

  const handleChange = (val) => {
    setText(val);
    if (!onTyping || !val.trim()) return;
    // Throttle: send a typing ping at most once per 1.5s
    if (typingDebounceRef.current) return;
    onTyping();
    typingDebounceRef.current = setTimeout(() => {
      typingDebounceRef.current = null;
    }, 1500);
  };

  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(lang === "pl" ? "pl-PL" : "en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        day: "2-digit",
        month: "short",
      }),
    [lang],
  );

  return (
    <div
      className={`flex flex-col rounded-2xl border border-slate-200 bg-white overflow-hidden min-h-0 ${
        fill ? "h-full flex-1" : "h-[60vh] sm:h-[520px]"
      }`}
      data-testid={`${testidPrefix}-panel`}
    >
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-slate-50">
        <div className="text-sm font-semibold text-slate-700 truncate">
          {peerName}
        </div>
        <div
          className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
            wsConnected
              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
              : "bg-amber-50 text-amber-700 border border-amber-200"
          }`}
          data-testid={`${testidPrefix}-status`}
        >
          {wsConnected ? (
            <>
              <Wifi className="w-3 h-3" /> live
            </>
          ) : (
            <>
              <WifiOff className="w-3 h-3" /> {t("portal.chat_offline_warning")}
            </>
          )}
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 space-y-3 bg-gradient-to-b from-sky-50/40 to-white"
        data-testid={`${testidPrefix}-messages`}
        // vaul drawers close when the user drags down. This attribute
        // tells vaul to ignore drag gestures inside the messages area so
        // scrolling through history doesn't accidentally dismiss the chat.
        data-vaul-no-drag=""
      >
        {introKey && (
          <p className="text-xs text-slate-500 text-center pb-2">
            {t(introKey)}
          </p>
        )}
        {messages.length === 0 && !peerTyping && (
          <div
            className="text-center text-sm text-slate-400 py-10"
            data-testid={`${testidPrefix}-empty`}
          >
            {t(emptyKey)}
          </div>
        )}
        {messages.map((m) => {
          const mine = m.sender === currentSender;
          return (
            <div
              key={m.id}
              className={`flex ${mine ? "justify-end" : "justify-start"}`}
              data-testid={`${testidPrefix}-msg-${m.id}`}
            >
              <div
                className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm shadow-sm ${
                  mine
                    ? "bg-sky-600 text-white rounded-br-md"
                    : "bg-white border border-slate-200 text-slate-800 rounded-bl-md"
                }`}
              >
                <div className="whitespace-pre-wrap break-words">{m.body}</div>
                <div
                  className={`text-[10px] mt-1 flex items-center gap-1 ${
                    mine ? "text-sky-100 justify-end" : "text-slate-400"
                  }`}
                >
                  <span>{dateFmt.format(new Date(m.created_at))}</span>
                  {mine &&
                    (m.read ? (
                      <CheckCheck
                        className="w-3.5 h-3.5"
                        data-testid={`${testidPrefix}-read-${m.id}`}
                      />
                    ) : (
                      <Check
                        className="w-3.5 h-3.5 opacity-70"
                        data-testid={`${testidPrefix}-sent-${m.id}`}
                      />
                    ))}
                </div>
              </div>
            </div>
          );
        })}
        {peerTyping && (
          <div className="flex justify-start" data-testid={`${testidPrefix}-typing`}>
            <div className="rounded-2xl rounded-bl-md bg-white border border-slate-200 px-3 py-2 shadow-sm">
              <div className="flex items-center gap-1 h-4">
                <Dot delay="0ms" />
                <Dot delay="150ms" />
                <Dot delay="300ms" />
              </div>
            </div>
          </div>
        )}
      </div>

      <form
        onSubmit={submit}
        className="flex gap-2 p-3 border-t border-slate-200 bg-white flex-shrink-0"
        // Prevent vaul drawer from interpreting a tap-then-drag inside
        // the form as a close gesture.
        data-vaul-no-drag=""
      >
        <textarea
          value={text}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => {
            // Auto-scroll the message list to the bottom on focus so the
            // latest message stays visible when the keyboard appears.
            // We deliberately do NOT call `scrollIntoView` on the textarea
            // — vaul + iOS handle that natively and our extra call was
            // racing with vaul's repositionInputs animation, leaving a
            // phantom gap above the keyboard.
            requestAnimationFrame(() => {
              const el = scrollRef.current;
              if (el) el.scrollTop = el.scrollHeight;
            });
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit(e);
            }
          }}
          placeholder={t(placeholderKey)}
          rows={1}
          // 16px+ font-size is critical: iOS Safari aggressively zooms in
          // on inputs with smaller fonts, which then breaks the drawer's
          // visual viewport calculations and leaves a phantom gap above
          // the keyboard. We size up to 16px on touch devices.
          className="flex-1 resize-none rounded-xl border border-slate-200 bg-white px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/40 max-h-32 min-w-0"
          data-testid={`${testidPrefix}-input`}
        />
        <button
          type="submit"
          disabled={!text.trim() || sending}
          className="btn-primary inline-flex items-center gap-1.5 px-3 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          data-testid={`${testidPrefix}-send-btn`}
        >
          <Send className="w-4 h-4" />
          <span className="hidden sm:inline">{t(sendKey)}</span>
        </button>
      </form>
    </div>
  );
}

function Dot({ delay }) {
  return (
    <span
      className="inline-block w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce"
      style={{ animationDelay: delay, animationDuration: "1s" }}
    />
  );
}
